import { Injectable, inject } from '@angular/core';

import { OfferSource } from '@cds';
import { DisplayType, FixtureParticipantType } from '@cds/betting-offer';
import { BetBuilderOptionGroupInfo } from '@cds/betting-offer/domain-specific/bet-builder';
import { OfferGroupV2, Tag } from '@cds/betting-offer/grouping/fixture-view';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventOptionGroup,
    EventOptionGroupType,
    EventOptionSet,
    ExtendedDisplayType,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { AngstromTagIdValues } from '@frontend/sports/betting-offer/feature/types';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { NativeAppService } from '@frontend/vanilla/core';
import { clone, isEqual, sortBy, toNumber, uniq, uniqWith } from 'lodash-es';

interface GroupingFactory {
    canProcess(source: OfferSource | undefined): boolean;

    getMarketGroupsConfig(grouping: EnhancedFixtureViewGroupingConfiguration): Record<string, OfferGroupV2 | undefined>;
}

export enum PreCreatedBuildABet {
    PreCreated = 'BAB',
}

class GameMarketGroupingFactory implements GroupingFactory {
    canProcess(source: OfferSource | undefined): boolean {
        return source === OfferSource.V1;
    }

    getMarketGroupsConfig(grouping: EnhancedFixtureViewGroupingConfiguration): Record<string, OfferGroupV2 | undefined> {
        return grouping.marketGroupsV2;
    }
}

class OptionMarketGroupingFactory implements GroupingFactory {
    canProcess(source: OfferSource | undefined): boolean {
        return source === OfferSource.V2;
    }

    getMarketGroupsConfig(grouping: EnhancedFixtureViewGroupingConfiguration): Record<string, OfferGroupV2 | undefined> {
        return grouping.marketGroupsV2;
    }
}

@Injectable({ providedIn: 'root' })
export class DetailedGroupingFactory {
    private nativeAppService = inject(NativeAppService);

    private factories = [new GameMarketGroupingFactory(), new OptionMarketGroupingFactory()];

    private readonly logger: SportsRemoteLogger;

    constructor() {
        const loggerFactory = inject(LoggerFactory);

        this.logger = loggerFactory.getLogger('DetailedGroupingFactory');
    }

    getOptionSets(
        optionGroups: EventOptionGroup[],
        grouping: EnhancedFixtureViewGroupingConfiguration,
        source: OfferSource | undefined,
        betBuilderId?: string,
        isLiveEvent?: boolean,
    ): EventOptionSet[] {
        const optionSets: EventOptionSet[] = [];
        const marketGroups = this.getMarketGroupsConfig(grouping, source);

        Object.values(marketGroups)
            .filter(isDefined)
            .sort((a, b) => a.order - b.order)
            .forEach((marketGroup) => {
                const selectedOptionGroups = optionGroups
                    .filter((optionGroup) => optionGroup.detailedGrouping.marketSet === marketGroup.order)
                    .filter((optionGroup) => (this.isSgpGroup(marketGroup) ? optionGroup.isBetBuilder : true));

                if (!selectedOptionGroups.length) {
                    return;
                }

                optionSets.push({
                    id: marketGroup.order,
                    name: this.isSgpGroup(marketGroup) ? (marketGroup.tag?.value ?? marketGroup.name) : marketGroup.name,
                    count: selectedOptionGroups.length,
                    storageId: marketGroup.id,
                    tags: this.getTags(selectedOptionGroups, marketGroup, grouping),
                    marketGroupTag: marketGroup.tag,
                    isPrecreated: selectedOptionGroups.every((og) => og.detailedGrouping?.displayType === ExtendedDisplayType.BetBuilder),
                    marketGroupSortId: marketGroup.order,
                    marketGroupInPlaySortId: marketGroup.inPlayOrder,
                });
            });

        if (!betBuilderId) {
            const sgpIndex = optionSets.findIndex((e) => e.marketGroupTag?.key === AngstromTagIdValues.Sgp && e.name === e.marketGroupTag?.value);
            if (sgpIndex !== -1) {
                optionSets.splice(sgpIndex, 1);
            }
        }

        if (this.nativeAppService.isTerminal && isLiveEvent) {
            const preCreatedIndex = optionSets.findIndex((e) => !!e.isPrecreated);
            if (preCreatedIndex !== -1) {
                // we will not have more than one precreated tab, as these are configured in the sports admin. so we are removing first occurence from the array
                optionSets.splice(preCreatedIndex, 1);
            }
        }

        return this.fakeMarketGroupingIfNotFound(optionGroups, optionSets);
    }

    groupMarkets(optionGroups: EventOptionGroup[], restrictPeriodGrouping?: boolean, restrictSorting?: boolean): EventOptionGroup[] {
        const groupedMarkets: EventOptionGroup[] = [];

        const preSortedOptionGroups: EventOptionGroup[] = [];
        if (!restrictSorting) {
            preSortedOptionGroups.push(...this.sortMarkets(optionGroups));
        } else {
            preSortedOptionGroups.push(...optionGroups);
        }

        const marketSetIds = uniq(preSortedOptionGroups.map((optionGroup) => optionGroup.detailedGrouping.marketSet));
        marketSetIds.forEach((setId) => {
            const marketGroupMarkets = preSortedOptionGroups.filter((optionGroup) => optionGroup.detailedGrouping.marketSet === setId);
            const groupedMarketGroupMarkets: EventOptionGroup[] = [];

            groupedMarketGroupMarkets.push(
                ...marketGroupMarkets.filter(
                    (market) => market.detailedGrouping.marketGroup === undefined && market.detailedGrouping.periodGroup === undefined,
                ),
            );
            if (restrictPeriodGrouping) {
                groupedMarketGroupMarkets.push(
                    ...marketGroupMarkets.filter(
                        (market) => market.detailedGrouping.marketGroup === undefined && market.detailedGrouping.periodGroup !== undefined,
                    ),
                );
            } else {
                groupedMarketGroupMarkets.push(
                    ...this.groupMarketsByPeriods(
                        marketGroupMarkets.filter(
                            (market) => market.detailedGrouping.marketGroup === undefined && market.detailedGrouping.periodGroup !== undefined,
                        ),
                    ),
                );
            }
            groupedMarketGroupMarkets.push(
                ...this.groupMarketsBySubGroups(marketGroupMarkets.filter((market) => market.detailedGrouping.marketGroup !== undefined)),
            );
            if (!restrictSorting) {
                groupedMarkets.push(...this.sortMarkets(groupedMarketGroupMarkets));
            } else {
                groupedMarkets.push(...groupedMarketGroupMarkets);
            }
        });

        return groupedMarkets;
    }

    transformEventGroupsPerPrecreatedGroup(
        optionGroups: EventOptionGroup[],
        precreatedOptionGroups: BetBuilderOptionGroupInfo[],
    ): EventOptionGroup[] {
        const transformedOptionGroups: EventOptionGroup[] = [];
        const marketSetIds = uniq(optionGroups.map((optionGroup) => optionGroup.detailedGrouping.marketSet));
        marketSetIds.forEach((setId) => {
            const optionGroupsBySetId = optionGroups.filter((optionGroup) => optionGroup.detailedGrouping.marketSet === setId);

            // we are transforming the event groups so that we end up with one event group per precreated group, with several options in it
            precreatedOptionGroups.forEach((precreatedGroup) => {
                const eventOptionGroups = optionGroupsBySetId.filter((market) =>
                    precreatedGroup.options.some((o) => o.marketId.toString() === market.id),
                );

                if (eventOptionGroups.length === 0) {
                    return;
                }

                const eventOptionsCount = uniq(
                    eventOptionGroups
                        .flatMap((og) => og.options)
                        .filter((op) => precreatedGroup.options.some((pgo) => op.id === pgo.id))
                        .map((option) => option.id),
                ).length;

                if (eventOptionsCount !== precreatedGroup.options.length) {
                    this.logger.warning(
                        `Precreated group '${precreatedGroup.groupId}' resolved with invalid amount of options: expected ${precreatedGroup.options.length}, resolved ${eventOptionsCount}, fixture: ${precreatedGroup.options[0].fixtureId}`,
                    );

                    return;
                }

                const newGroupWithAllOptions = this.groupOptions(eventOptionGroups);
                newGroupWithAllOptions.id = precreatedGroup.groupId;
                transformedOptionGroups.push(newGroupWithAllOptions);
            });
        });

        return transformedOptionGroups;
    }

    createSubGroup(markets: EventOptionGroup[]): EventOptionGroup {
        const subGroupMarkets = this.sortSubGroupMarkets(markets);

        return this.groupOptions(subGroupMarkets);
    }

    private fakeMarketGroupingIfNotFound(optionGroups: EventOptionGroup[], optionSets: EventOptionSet[]): EventOptionSet[] {
        if (!optionSets.length && optionGroups.length) {
            // eslint-disable-next-line no-param-reassign
            optionGroups.forEach((g) => (g.detailedGrouping.marketSet = 10000));

            optionSets.push({
                id: 10000,
                name: 'All',
                count: optionGroups.length,
                storageId: '',
            });
        }

        return optionSets;
    }

    private groupMarketsBySubGroups(markets: EventOptionGroup[]): EventOptionGroup[] {
        const groupedMarkets: EventOptionGroup[] = [];
        const subGroupIds = uniq(markets.map((market) => this.getSubGroupId(market)));

        subGroupIds.forEach((subGroupId) => {
            const subGroupMarkets = markets.filter((market) => this.getSubGroupId(market) === subGroupId);
            const periodOptionGroup = this.getPeriodOptionGroup(subGroupMarkets);
            if (periodOptionGroup) {
                groupedMarkets.push(periodOptionGroup);
            } else {
                groupedMarkets.push(this.createSubGroup(subGroupMarkets));
            }
        });

        return groupedMarkets;
    }

    private getSubGroupId(market: EventOptionGroup): string | number {
        if (market.subPeriodNumber) {
            return `${market.detailedGrouping.marketOrder}sub-period${market.subPeriodNumber}`;
        }

        if (market.rangeValue) {
            return `${market.detailedGrouping.marketOrder}${market.rangeValue}`;
        }

        if (market.participantIds) {
            if (market.detailedGrouping.displayType === DisplayType.Head2HeadSixPack) {
                return `${market.parameters?.find((p) => p.key === Tv2MarketParameters.Happening)?.value}${market.detailedGrouping.displayType}${market.participantIds}`;
            }

            return `${market.detailedGrouping.marketOrder}${market.participantIds}`;
        }

        const fixtureParticipant = market.parameters?.find((param) => param.key === Tv2MarketParameters.FixtureParticipant);

        if (market.detailedGrouping.fixtureParticipantType === FixtureParticipantType.Other && fixtureParticipant) {
            return `${market.detailedGrouping.marketOrder}${fixtureParticipant.value}`;
        }

        return market.detailedGrouping.marketOrder;
    }

    protected getPeriodOptionGroup(markets: EventOptionGroup[]): EventOptionGroup | undefined {
        if (!markets) {
            return;
        }
        const periodMarkets = this.createEventOptionGroupByPeriodIdDictionary(markets);
        const periodKeys = sortBy(
            Object.keys(periodMarkets).map((x) => {
                return toNumber(x);
            }),
        );

        const periodGroupedMarkets: EventOptionGroup[] = [];
        if (periodKeys.length > 1) {
            periodKeys.forEach((k) => {
                periodGroupedMarkets.push(this.createSubGroup(periodMarkets[k]));
            });

            return this.createPeriodOptionGroup(periodGroupedMarkets[0], periodGroupedMarkets);
        } else if (periodKeys.length === 1) {
            periodGroupedMarkets[0] = this.groupOptions(periodMarkets[periodKeys[0]]);

            return this.singlePeriodOptionGroup(periodGroupedMarkets[0]);
        }

        return undefined;
    }

    protected createEventOptionGroupByPeriodIdDictionary(optionGroups: EventOptionGroup[]): Record<number, EventOptionGroup[]> {
        const res: EventOptionGroup[][] = [];
        for (const optionGroup of optionGroups) {
            if (typeof optionGroup.detailedGrouping.periodGroup === 'undefined') {
                continue;
            }
            res[optionGroup.detailedGrouping.periodGroup] = res[optionGroup.detailedGrouping.periodGroup] || [];
            res[optionGroup.detailedGrouping.periodGroup].push(optionGroup);
        }

        return res;
    }

    private createEventOptionGroupByMarketOrderIdWithPeriodDictionary(optionGroups: EventOptionGroup[]): EventOptionGroup[][] {
        const res: EventOptionGroup[][] = [];
        for (const optionGroup of optionGroups) {
            if (typeof optionGroup.detailedGrouping.marketOrder === 'undefined' || typeof optionGroup.detailedGrouping.periodGroup === 'undefined') {
                continue;
            }
            res[optionGroup.detailedGrouping.marketOrder] = res[optionGroup.detailedGrouping.marketOrder] || [];
            res[optionGroup.detailedGrouping.marketOrder].push(optionGroup);
        }

        return res;
    }

    private groupMarketsByPeriods(markets: EventOptionGroup[]): EventOptionGroup[] {
        const groupedMarkets: EventOptionGroup[] = [];

        const marketOrderMarkets = this.createEventOptionGroupByMarketOrderIdWithPeriodDictionary(markets);

        marketOrderMarkets.forEach((k) => {
            const periodOptionGroup = this.getPeriodOptionGroup(k);
            if (periodOptionGroup) {
                groupedMarkets.push(periodOptionGroup);
            } else {
                if (k[0]) {
                    groupedMarkets.push(k[0]);
                }
            }
        });

        return groupedMarkets;
    }

    protected singlePeriodOptionGroup(periodGroup: EventOptionGroup): EventOptionGroup {
        // eslint-disable-next-line no-param-reassign
        periodGroup.id = this.getGroupId(periodGroup.detailedGrouping.displayType, periodGroup.id);
        // eslint-disable-next-line no-param-reassign
        periodGroup.name = this.getOptionGroupName(periodGroup);

        return periodGroup;
    }

    protected createPeriodOptionGroup(root: EventOptionGroup, subGroups: EventOptionGroup[]): EventOptionGroup {
        const periodGroup = clone(root);
        periodGroup.id = this.getGroupId(periodGroup.detailedGrouping.displayType, periodGroup.id);
        periodGroup.options = [];
        // eslint-disable-next-line no-param-reassign
        subGroups.forEach((s) => (s.type = EventOptionGroupType.Period));
        periodGroup.subGroups = subGroups;
        periodGroup.name = this.getOptionGroupName(periodGroup, true);

        if (
            periodGroup.detailedGrouping.displayType === DisplayType.PlayerMilestone ||
            periodGroup.detailedGrouping.displayType === DisplayType.PlayerMilestoneOverUnder
        ) {
            periodGroup.online = subGroups.some((x) => x.online);
            periodGroup.visible = subGroups.some((x) => x.visible);
        }

        return periodGroup;
    }

    protected groupOptions(markets: EventOptionGroup[]): EventOptionGroup {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const firstMarket = markets.shift()!;
        const optionGroup = Object.assign(new EventOptionGroup(), {
            ...firstMarket,
            options: [...firstMarket.options],
        });
        optionGroup.id = this.getGroupId(optionGroup.detailedGrouping.displayType, optionGroup.id, 'group');
        optionGroup.originalId = firstMarket.id;

        if (!markets.length) {
            if (this.isPlayer_OptionGroup(optionGroup)) {
                optionGroup.name = this.getOptionGroupName(optionGroup);
            }

            return optionGroup;
        }

        optionGroup.groupedMarkets = markets.map((m) => m.id);

        optionGroup.groupedTemplateIds = markets.map((m) => m.templateId);
        markets.forEach((market) => {
            optionGroup.options.push(...market.options);
        });

        optionGroup.online = optionGroup.options.some((group) => group.online);
        optionGroup.name = this.getOptionGroupName(optionGroup);

        return optionGroup;
    }

    private sortSubGroupMarkets(markets: EventOptionGroup[]): EventOptionGroup[] {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return markets.sort((first, second) => first.detailedGrouping.marketGroup! - second.detailedGrouping.marketGroup!);
    }

    private sortMarkets(markets: EventOptionGroup[]): EventOptionGroup[] {
        return markets
            .sort((first, second) => +first.id.slice(2) - +second.id.slice(2))
            .sort((first, second) => first.detailedGrouping.marketOrder - second.detailedGrouping.marketOrder);
    }

    private getMarketGroupsConfig(
        grouping: EnhancedFixtureViewGroupingConfiguration,
        source: OfferSource | undefined,
    ): Record<string, OfferGroupV2 | undefined> {
        for (const factory of this.factories) {
            if (factory.canProcess(source)) {
                return factory.getMarketGroupsConfig(grouping);
            }
        }

        throw new Error('No grouping factory found for grouping configuration');
    }

    // eslint-disable-next-line complexity
    private getOptionGroupName(optionGroup: EventOptionGroup, isPeriodOptionGroup = false): string {
        if (optionGroup.detailedGrouping.periodGroup !== undefined && !optionGroup.subGroups) {
            switch (optionGroup.detailedGrouping.displayType) {
                case DisplayType.RaceTo:
                case DisplayType.BTTS:
                case DisplayType.Outrights:
                case DisplayType.TotalScore:
                case DisplayType.YesNo:
                case DisplayType.Spread:
                    return optionGroup.detailedGrouping.name || optionGroup.name;
                case DisplayType.Regular:
                case DisplayType.CorrectScore:
                case DisplayType.OverUnder:
                    return optionGroup.name;
                case DisplayType.PlayerProps:
                case DisplayType.SixPack:
                    return optionGroup.sixPackGridGroupId
                        ? isPeriodOptionGroup
                            ? optionGroup.detailedGrouping.name || optionGroup.periodName || optionGroup.name
                            : optionGroup.periodName || optionGroup.name
                        : `${optionGroup.detailedGrouping.name || optionGroup.name} - ${optionGroup.periodName}`;
                default:
                    return optionGroup.detailedGrouping.name || optionGroup.name;
            }
        }
        if (optionGroup.detailedGrouping.name === PreCreatedBuildABet.PreCreated) {
            return optionGroup.name || optionGroup.detailedGrouping.name;
        } else {
            return optionGroup.detailedGrouping.name || optionGroup.name;
        }
    }

    private getGroupId(displayType: DisplayType | ExtendedDisplayType, groupId: string, groupType = 'period'): string {
        return `${groupType}-${displayType}-${groupId}`;
    }

    private isPlayer_OptionGroup(optionGroup: EventOptionGroup): boolean {
        const displayType = optionGroup.detailedGrouping.displayType;

        return (
            displayType === DisplayType.PlayerMilestone ||
            displayType === DisplayType.PlayerCombined ||
            displayType === DisplayType.PlayerStats ||
            displayType === DisplayType.PlayerMilestoneOverUnder
        );
    }

    private isSgpGroup(group: OfferGroupV2): boolean {
        return group.tag?.key === AngstromTagIdValues.Sgp;
    }

    private getTags(selectedOptionGroups: EventOptionGroup[], marketGroup: OfferGroupV2, grouping: EnhancedFixtureViewGroupingConfiguration): Tag[] {
        const availableTags = selectedOptionGroups.flatMap((gr) => gr.detailedGrouping.tags || []);
        const tags = availableTags ? uniqWith(availableTags, isEqual) : [];

        if (this.isSgpGroup(marketGroup)) {
            const tagOrder = this.getTagOrder(grouping);
            if (tagOrder) {
                this.sortTags(tags, tagOrder);
            }
        }

        return tags;
    }

    private getTagOrder(grouping: EnhancedFixtureViewGroupingConfiguration): Tag[] | undefined {
        const sgpMarketGroup = grouping?.marketGroupsV2
            ? Object.values(grouping.marketGroupsV2).find((x) => x?.tag?.key === AngstromTagIdValues.Sgp)
            : undefined;

        if (sgpMarketGroup) {
            const allTags = sgpMarketGroup.marketGroupItems ? Object.values(sgpMarketGroup.marketGroupItems).flatMap((x) => x?.tags ?? []) : [];
            const uniqueTags = allTags ? uniqWith(allTags, isEqual) : [];

            if (uniqueTags.length > 0 && uniqueTags[0].key === AngstromTagIdValues.Popular) {
                return uniqueTags;
            }

            return uniqueTags.sort((a, b) => (a.key === AngstromTagIdValues.Popular ? -1 : b.key === AngstromTagIdValues.Popular ? 1 : 0));
        }

        return undefined;
    }

    private sortTags(tags: Tag[], tagsInOrder: Tag[]): Tag[] | undefined {
        const tagOrder = new Map(tagsInOrder.map((tag, index) => [tag.key, index]));

        return tags.sort((a, b) => {
            const indexA = tagOrder.get(a.key) ?? Number.MAX_SAFE_INTEGER;
            const indexB = tagOrder.get(b.key) ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
        });
    }
}
